/**
 * Day Selector Component for Trip Map
 * Allows users to navigate between trip days with swipe gestures or tap navigation
 * Auto-detects current day based on travel dates
 */

import React, { useMemo, useRef, useCallback } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Pressable,
  Platform,
  Animated,
  PanResponder,
} from 'react-native';
import { useTheme } from '@/theme';
import { ThemedText, Caption } from '@/components/ui/Text';
import { Ionicons } from '@expo/vector-icons';

const normalizeHexColor = (value: string): string | null => {
  const v = value.trim();
  if (!v.startsWith('#')) return null;
  const hex = v.slice(1);
  if (hex.length === 3) {
    const r = hex[0];
    const g = hex[1];
    const b = hex[2];
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  if (hex.length >= 6) {
    // Ignore alpha if present (#RRGGBBAA).
    return `#${hex.slice(0, 6)}`.toUpperCase();
  }
  return null;
};

const getOnColor = (backgroundColor: string, fallback: 'light' | 'dark' = 'light'): '#FFFFFF' | '#000000' => {
  const hex = normalizeHexColor(backgroundColor);
  if (!hex) {
    return fallback === 'dark' ? '#000000' : '#FFFFFF';
  }

  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const R = toLinear(r);
  const G = toLinear(g);
  const B = toLinear(b);
  const luminance = 0.2126 * R + 0.7152 * G + 0.0722 * B;

  // If the background is light, use black text; otherwise use white.
  return luminance > 0.6 ? '#000000' : '#FFFFFF';
};

export type TripDay = {
  date: Date;
  dayNumber: number;
  label: string;           // "Day 1", "Day 2"
  dateLabel: string;       // "Thu, Dec 19"
  isToday: boolean;
  isPast: boolean;
  isFuture: boolean;
  activityCount: number;
  hasActivities: boolean;
  cityName?: string;
  cityCode?: string;
  cityIndex?: number;
};

export type DaySelectorProps = {
  tripDays: TripDay[];
  selectedDayIndex: number;
  onSelectDay: (index: number) => void;
  onSwipeDay?: (direction: 'prev' | 'next') => void;
  showAllDaysOption?: boolean;
  variant?: 'dots' | 'pills';
  compact?: boolean;
  showCityBadge?: boolean;
};

/**
 * Get the current day index based on today's date
 */
export const getCurrentDayIndex = (tripDays: TripDay[]): number => {
  if (!tripDays || tripDays.length === 0) return 0;
  
  const todayIndex = tripDays.findIndex(d => d.isToday);
  if (todayIndex >= 0) return todayIndex;
  
  // Trip not started yet: find first future day
  const firstFutureIndex = tripDays.findIndex(d => d.isFuture);
  if (firstFutureIndex >= 0) return firstFutureIndex;
  
  // Trip ended: return last day
  return tripDays.length - 1;
};

/**
 * Pills variant - horizontal scrollable day pills
 */
const PillsSelector: React.FC<DaySelectorProps> = ({
  tripDays,
  selectedDayIndex,
  onSelectDay,
  showAllDaysOption = true,
  compact = false,
  showCityBadge = false,
}) => {
  const theme = useTheme();
  const isMobile = Platform.OS !== 'web';
  const onPrimary = useMemo(
    () => getOnColor(theme.colors.primary, theme.colorScheme === 'dark' ? 'dark' : 'light'),
    [theme.colors.primary, theme.colorScheme],
  );
  const onPrimaryMuted = onPrimary === '#000000' ? 'rgba(0,0,0,0.72)' : 'rgba(255,255,255,0.8)';
  const onPrimaryFaint = onPrimary === '#000000' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)';
  const selectedChipBg = theme.colors.primary;
  const selectedChipBorder = theme.colors.primary;
  const selectedBadgeBg = onPrimary === '#000000' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.18)';
  const selectedBadgeBorder = onPrimary === '#000000' ? 'rgba(0,0,0,0.22)' : 'rgba(255,255,255,0.35)';
  const selectedCountBg = onPrimary === '#000000' ? 'rgba(0,0,0,0.14)' : 'rgba(255,255,255,0.25)';
  const scrollViewRef = useRef<ScrollView>(null);
  
  // Auto-scroll to selected day
  const handleSelectDay = useCallback((index: number) => {
    onSelectDay(index);
    // Scroll to center the selected pill
    scrollViewRef.current?.scrollTo({
      x: Math.max(0, (index + (showAllDaysOption ? 1 : 0)) * 90 - 100),
      animated: true,
    });
  }, [onSelectDay, showAllDaysOption]);
  
  return (
    <ScrollView
      ref={scrollViewRef}
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={[styles.pillsContainer, isMobile && styles.pillsContainerMobile]}
    >
      {/* "All Days" option */}
      {showAllDaysOption && (
        <Pressable
          onPress={() => handleSelectDay(-1)}
          style={({ pressed }) => [
            styles.pill,
            isMobile && styles.pillMobile,
            compact && styles.pillCompact,
            compact && isMobile && styles.pillCompactMobile,
            {
              backgroundColor: selectedDayIndex === -1
                ? theme.colors.primary
                : theme.colors.surface,
              borderColor: selectedDayIndex === -1
                ? theme.colors.primary
                : theme.colors.borderMuted,
              opacity: pressed ? 0.8 : 1,
            },
          ]}
          accessibilityRole="button"
          accessibilityLabel={`All days${selectedDayIndex === -1 ? ', selected' : ''}`}
          accessibilityHint="Shows activities from every day in this trip"
          accessibilityState={{ selected: selectedDayIndex === -1 }}
        >
          <Ionicons
            name="calendar-outline"
            size={14}
            color={selectedDayIndex === -1 ? onPrimary : theme.colors.textSecondary}
          />
          <ThemedText
            variant="caption"
            style={[
              styles.pillText,
              { color: selectedDayIndex === -1 ? onPrimary : theme.colors.text },
            ]}
          >
            All
          </ThemedText>
          {selectedDayIndex === -1 && (
            <Ionicons name="checkmark-circle" size={14} color={onPrimary} />
          )}
        </Pressable>
      )}
      
      {tripDays.map((day, index) => {
        const isSelected = index === selectedDayIndex;
        const badgeLabel = showCityBadge
          ? day.cityCode || (day.cityName ? day.cityName.split(',')[0] : undefined)
          : undefined;
        const previousCityIndex = index > 0 ? tripDays[index - 1].cityIndex : null;
        const isCityBoundary = showCityBadge && previousCityIndex !== null && previousCityIndex !== day.cityIndex;
        
        return (
          <View
            key={`day-${day.dayNumber}`}
            style={[styles.pillWrapper, isCityBoundary && styles.cityBoundary]}
          >
            <Pressable
              onPress={() => handleSelectDay(index)}
              style={({ pressed }) => [
                styles.pill,
                isMobile && styles.pillMobile,
                compact && styles.pillCompact,
                compact && isMobile && styles.pillCompactMobile,
                {
                  backgroundColor: isSelected ? selectedChipBg : theme.colors.surface,
                  borderColor: isSelected ? selectedChipBorder : theme.colors.borderMuted,
                  opacity: pressed ? 0.8 : 1,
                },
              ]}
              accessibilityRole="button"
              accessibilityLabel={`${day.label}, ${day.dateLabel}${isSelected ? ', selected' : ''}`}
              accessibilityHint="Selects this trip day"
              accessibilityState={{ selected: isSelected }}
            >
              {/* Today indicator dot */}
              {day.isToday && (
                <View
                  style={[
                    styles.todayDot,
                    { backgroundColor: isSelected ? onPrimary : theme.colors.success },
                  ]}
                />
              )}
              
              {/* Day content */}
              <View style={styles.pillContent}>
                <ThemedText
                  variant="caption"
                  style={[
                    styles.pillText,
                    { 
                      color: isSelected ? onPrimary : theme.colors.text,
                      fontWeight: '600',
                    },
                  ]}
                  numberOfLines={1}
                >
                  {day.label}
                </ThemedText>
                {badgeLabel ? (
                  <View
                    style={[
                      styles.cityBadgeChip,
                      isSelected
                        ? { backgroundColor: selectedBadgeBg, borderColor: selectedBadgeBorder }
                        : {
                            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.surface : '#F4F6F6',
                            borderColor: theme.colorScheme === 'dark' ? theme.colors.borderMuted : '#D6E0E3',
                          },
                    ]}
                  >
                    <Caption
                      color={isSelected ? onPrimary : theme.colors.primary}
                      style={styles.cityBadgeText}
                      numberOfLines={1}
                    >
                      {badgeLabel.toUpperCase()}
                    </Caption>
                  </View>
                ) : null}
                
                {!compact && (
                  <Caption
                    color={isSelected ? onPrimaryMuted : theme.colors.textSecondary}
                    style={styles.dateText}
                    numberOfLines={1}
                  >
                    {day.dateLabel}
                  </Caption>
                )}
              </View>
              
              {/* Activity count badge */}
              {day.activityCount > 0 && (
                <View
                  style={[
                    styles.countBadge,
                    {
                      backgroundColor: isSelected
                        ? selectedCountBg
                        : theme.colors.primary + '20',
                    },
                  ]}
                >
                  <Caption
                    color={isSelected ? onPrimary : theme.colors.primary}
                    style={styles.countText}
                  >
                    {day.activityCount}
                  </Caption>
                </View>
              )}
              
              {/* Empty day indicator */}
              {!day.hasActivities && !day.isToday && (
                <View style={styles.emptyIndicator}>
                  <Ionicons
                    name="add-circle-outline"
                    size={12}
                    color={isSelected ? onPrimaryFaint : theme.colors.textSecondary}
                  />
                </View>
              )}
              {isSelected && (
                <View style={styles.selectedIndicator}>
                  <Ionicons name="checkmark-circle" size={14} color={onPrimary} />
                </View>
              )}
            </Pressable>
          </View>
        );
      })}
    </ScrollView>
  );
};

/**
 * Dots variant - compact dot indicators with swipe area
 */
const DotsSelector: React.FC<DaySelectorProps> = ({
  tripDays,
  selectedDayIndex,
  onSelectDay,
  onSwipeDay,
  showAllDaysOption = false,
}) => {
  const theme = useTheme();
  const onPrimary = useMemo(
    () => getOnColor(theme.colors.primary, theme.colorScheme === 'dark' ? 'dark' : 'light'),
    [theme.colors.primary, theme.colorScheme],
  );
  const pan = useRef(new Animated.Value(0)).current;
  
  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onMoveShouldSetPanResponder: (_, gesture) => {
          // Only capture horizontal swipes
          return Math.abs(gesture.dx) > Math.abs(gesture.dy) && Math.abs(gesture.dx) > 15;
        },
        onPanResponderMove: (_, gesture) => {
          pan.setValue(gesture.dx);
        },
        onPanResponderRelease: (_, gesture) => {
          const SWIPE_THRESHOLD = 40;
          
          if (gesture.dx > SWIPE_THRESHOLD && selectedDayIndex > (showAllDaysOption ? -1 : 0)) {
            onSwipeDay?.('prev');
            onSelectDay(selectedDayIndex - 1);
          } else if (gesture.dx < -SWIPE_THRESHOLD && selectedDayIndex < tripDays.length - 1) {
            onSwipeDay?.('next');
            onSelectDay(selectedDayIndex + 1);
          }
          
          Animated.spring(pan, {
            toValue: 0,
            useNativeDriver: true,
            tension: 50,
            friction: 7,
          }).start();
        },
      }),
    [pan, selectedDayIndex, tripDays.length, onSelectDay, onSwipeDay, showAllDaysOption],
  );
  
  const totalDots = tripDays.length + (showAllDaysOption ? 1 : 0);
  const effectiveIndex = selectedDayIndex + (showAllDaysOption ? 1 : 0);
  
  return (
    <Animated.View
      style={[
        styles.dotsContainer,
        { transform: [{ translateX: pan }] },
      ]}
      {...panResponder.panHandlers}
    >
      {/* Navigation hint */}
      <View style={styles.swipeHintContainer}>
        {selectedDayIndex > (showAllDaysOption ? -1 : 0) && (
          <Ionicons
            name="chevron-back"
            size={16}
            color={theme.colors.textSecondary}
            style={styles.swipeHintIcon}
          />
        )}
        
        {/* Day dots */}
        <View style={styles.dotsRow}>
          {showAllDaysOption && (
            <Pressable
              onPress={() => onSelectDay(-1)}
              accessibilityRole="button"
              accessibilityLabel={`All days${selectedDayIndex === -1 ? ', selected' : ''}`}
              accessibilityState={{ selected: selectedDayIndex === -1 }}
            >
              <View
                style={[
                  styles.dot,
                  selectedDayIndex === -1 && styles.dotSelected,
                  {
                    backgroundColor: selectedDayIndex === -1
                      ? theme.colors.primary
                      : theme.colors.borderMuted,
                  },
                ]}
              />
            </Pressable>
          )}
          
          {tripDays.map((day, index) => {
            const isSelected = index === selectedDayIndex;
            
            return (
              <Pressable
                key={`dot-${day.dayNumber}`}
                onPress={() => onSelectDay(index)}
                accessibilityRole="button"
                accessibilityLabel={`${day.label}, ${day.dateLabel}${isSelected ? ', selected' : ''}`}
                accessibilityState={{ selected: isSelected }}
              >
                <View
                  style={[
                    styles.dot,
                    isSelected && styles.dotSelected,
                    day.isToday && !isSelected && styles.dotToday,
                    {
                      backgroundColor: isSelected
                        ? theme.colors.primary
                        : day.isToday
                          ? theme.colors.success
                          : day.hasActivities
                            ? theme.colors.textSecondary
                            : theme.colors.borderMuted,
                    },
                  ]}
                >
                  {day.isToday && isSelected && (
                    <View style={[styles.dotInner, { backgroundColor: onPrimary }]} />
                  )}
                </View>
              </Pressable>
            );
          })}
        </View>
        
        {selectedDayIndex < tripDays.length - 1 && (
          <Ionicons
            name="chevron-forward"
            size={16}
            color={theme.colors.textSecondary}
            style={styles.swipeHintIcon}
          />
        )}
      </View>
      
      {/* Current day label */}
      <Caption color={theme.colors.textSecondary} style={styles.dotsLabel}>
        {selectedDayIndex === -1
          ? 'All Days'
          : tripDays[selectedDayIndex]?.isToday
            ? `Today • ${tripDays[selectedDayIndex]?.dateLabel}`
            : `${tripDays[selectedDayIndex]?.label} • ${tripDays[selectedDayIndex]?.dateLabel}`}
      </Caption>
    </Animated.View>
  );
};

/**
 * Main DaySelector component
 */
export const DaySelector: React.FC<DaySelectorProps> = (props) => {
  const { variant = 'pills', tripDays } = props;
  
  // Don't render if no days or single day trip
  if (!tripDays || tripDays.length <= 1) {
    return null;
  }
  
  switch (variant) {
    case 'dots':
      return <DotsSelector {...props} />;
    case 'pills':
    default:
      return <PillsSelector {...props} />;
  }
};

const styles = StyleSheet.create({
  // Pills styles
  pillsContainer: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    gap: 8,
    flexDirection: 'row',
  },
  pillsContainerMobile: {
    paddingVertical: 5,
    gap: 6,
  },
  pillWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cityBoundary: {
    marginLeft: 12,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    gap: 6,
    minWidth: 80,
  },
  pillMobile: {
    paddingVertical: 6,
  },
  pillCompact: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    minWidth: 60,
  },
  pillCompactMobile: {
    paddingVertical: 5,
  },
  pillContent: {
    flexDirection: 'column',
    minWidth: 0,
  },
  pillText: {
    fontSize: 13,
  },
  cityBadgeChip: {
    marginTop: 2,
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    borderWidth: 1,
  },
  cityBadgeChipSelected: {
    backgroundColor: 'rgba(255,255,255,0.18)',
    borderColor: 'rgba(255,255,255,0.35)',
  },
  cityBadgeChipDefault: {
    backgroundColor: '#F4F6F6',
    borderColor: '#D6E0E3',
  },
  cityBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.2,
  },
  dateText: {
    fontSize: 10,
    marginTop: 1,
  },
  todayDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    position: 'absolute',
    top: -2,
    right: -2,
  },
  countBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 10,
    marginLeft: 4,
  },
  countText: {
    fontSize: 10,
    fontWeight: '700',
  },
  emptyIndicator: {
    marginLeft: 2,
  },
  selectedIndicator: {
    marginLeft: 4,
  },
  
  // Dots styles
  dotsContainer: {
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  swipeHintContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  swipeHintIcon: {
    opacity: 0.5,
  },
  dotsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 8,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dotSelected: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  dotToday: {
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.5)',
  },
  dotInner: {
    width: 4,
    height: 4,
    borderRadius: 2,
  },
  dotsLabel: {
    marginTop: 8,
    fontSize: 12,
    textAlign: 'center',
  },
});

export default DaySelector;
